/**
 * Sticky top bar: the menu open/close toggle, page title, theme switch, the
 * alert bell and the avatar, which opens the profile menu.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store/AppContext.jsx';
import Icon from '../lib/icons.jsx';
import { initials, avatarClass } from '../lib/format.js';
import { PAGE_META } from '../constants/defaults.js';
import AlertsDropdown from './AlertsDropdown.jsx';
import { exportBackup } from '../lib/csv.js';
import { signOut } from '../lib/session.js';

export default function Topbar({ view }) {
  const { S, rows, crm, setTheme, navHidden, setNavHidden, unread, stats, lockNow, me } = useApp();

  /* Their own name where there is one. Offline there is nobody to ask, so
     the workspace's own name stands in rather than an empty circle. */
  const who = me?.name || S.userName || 'Signed in';
  /* Whose workspace it is. Everybody else - Admin included - uses it, and
     is offered their own account rather than its controls. Offline there is
     no role to read, and no server to refuse anything either. */
  const owner = !me || me.role === 'Owner';
  const navigate = useNavigate();

  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  const avaRef = useRef(null);

  /* Clicking anywhere else, or Escape, closes it. */
  useEffect(() => {
    if (!menu) return undefined;
    const onDocClick = e => {
      if (menuRef.current?.contains(e.target) || avaRef.current?.contains(e.target)) return;
      setMenu(false);
    };
    const onEsc = e => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menu]);

  const go = path => { setMenu(false); navigate(path); };

  const title = (PAGE_META[view] || ['Dashboard'])[0];
  const badge = unread || stats.open;

  return (
    <div className="top">
      <button
        type="button"
        className="iconbtn"
        aria-label={navHidden ? 'Open menu' : 'Close menu'}
        aria-expanded={!navHidden}
        title={navHidden ? 'Open the menu' : 'Close the menu'}
        onClick={() => setNavHidden(h => !h)}
      >
        <Icon name={navHidden ? 'menu' : 'x'} />
      </button>

      <h1>{title}</h1>
      <div className="spacer" />

      <button
        type="button"
        className="iconbtn"
        aria-label="Toggle theme"
        title="Light or dark mode"
        onClick={() => setTheme(S.theme === 'dark' ? 'light' : 'dark')}
      >
        <Icon name={S.theme === 'dark' ? 'sun' : 'moon'} />
      </button>

      <AlertsDropdown badge={badge} />

      <div className="pmwrap">
        <button
          type="button"
          className="avatar"
          title="Your account"
          aria-haspopup="menu"
          aria-expanded={menu}
          ref={avaRef}
          onClick={() => setMenu(o => !o)}
        >
          {initials(who)}
        </button>

        <div className={`pmenu${menu ? ' open' : ''}`} ref={menuRef} role="menu">
          {/*
            * Whoever is signed in, not the workspace.
            *
            * This read `S.userName`, which is a workspace setting - the same
            * name for everybody who opens it - over `S.workspace`, which is
            * the workspace again. So an Agent was greeted as "Client Ledger,
            * Client Ledger" and shown the owner's tools underneath.
            */}
          <div className="pmhead">
            <span className={`avatar ${avatarClass(who)}`}>{initials(who)}</span>
            <div>
              <b>{who}</b>
              <span>{me?.email || S.workspace || ''}</span>
              {me?.role ? <em className="pmrole">{me.role}</em> : null}
            </div>
          </div>

          <button type="button" onClick={() => go('/settings/profile')}>
            <Icon name="users" />Profile
          </button>

          {/*
            * The workspace's own controls, for the people who own it.
            *
            * Settings edits how the workspace behaves for everybody, and a
            * backup is the whole book in one file. Neither belongs to an
            * Agent or a Viewer, and offering them only to have the screen
            * refuse teaches people the menu is not to be trusted.
            */}
          {owner ? (
            <>
              <button type="button" onClick={() => go('/settings')}>
                <Icon name="gear" />Settings
              </button>
              <button
                type="button"
                onClick={() => {
                  setMenu(false);
                  exportBackup({ settings: S, clients: rows, crm }, S.dateFmt);
                }}
              >
                <Icon name="down" />Download a backup
              </button>
              {S.pinHash ? (
                <button type="button" onClick={() => { setMenu(false); lockNow(); }}>
                  <Icon name="lock" />Lock the workspace
                </button>
              ) : null}
            </>
          ) : null}

          <button type="button" className="danger" onClick={() => { setMenu(false); signOut(); }}>
            <Icon name="out" />Log out
          </button>
        </div>
      </div>
    </div>
  );
}
